import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, FileText, Trash2, AlertCircle } from "lucide-react";

/**
 * CSV-Import-Modal für Bulk-Spieleranlage.
 * Erwartet Spalten (Header optional): name, position, jersey_number
 * Beispiel:
 *   name,position,jersey_number
 *   Max Mustermann,Sechser,8
 *   Lena Schmidt,Torwart,1
 */
export default function CsvImportModal({ teamId, onClose, onImported }) {
  const [csvText, setCsvText] = useState("");
  const [parsed, setParsed] = useState(null); // {rows: [], errors: []}
  const [importing, setImporting] = useState(false);

  const parseCsv = (text) => {
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length === 0) return { rows: [], errors: [] };

    // Header-Erkennung (case-insensitive)
    const first = lines[0].toLowerCase();
    const hasHeader =
      first.includes("name") &&
      (first.includes("position") || first.includes("nummer") || first.includes("jersey"));
    let headerCols = null;
    let dataLines = lines;
    if (hasHeader) {
      headerCols = lines[0].split(/[,;\t]/).map((c) => c.trim().toLowerCase());
      dataLines = lines.slice(1);
    }

    const errors = [];
    const rows = dataLines.map((line, idx) => {
      const parts = line.split(/[,;\t]/).map((c) => c.trim());
      let name = "";
      let position = "";
      let jersey_number = "";
      if (headerCols) {
        const nameIdx = headerCols.findIndex((c) => c === "name" || c === "spieler");
        const posIdx = headerCols.findIndex((c) => c === "position" || c === "pos");
        const numIdx = headerCols.findIndex(
          (c) => c === "jersey_number" || c === "nummer" || c === "rueckennummer" || c === "#"
        );
        name = parts[nameIdx] || "";
        position = posIdx >= 0 ? parts[posIdx] || "" : "";
        jersey_number = numIdx >= 0 ? parts[numIdx] || "" : "";
      } else {
        [name = "", position = "", jersey_number = ""] = parts;
      }
      const row = {
        name: name.trim(),
        position: position.trim() || null,
        jersey_number:
          jersey_number && !isNaN(parseInt(jersey_number, 10))
            ? parseInt(jersey_number, 10)
            : null,
      };
      if (!row.name) {
        errors.push(`Zeile ${idx + 1 + (hasHeader ? 1 : 0)}: Name fehlt`);
      }
      return row;
    });
    return { rows: rows.filter((r) => r.name), errors };
  };

  const handleParse = () => {
    setParsed(parseCsv(csvText));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      setCsvText(text);
      setParsed(parseCsv(text));
    };
    reader.readAsText(file);
  };

  const removeRow = (idx) => {
    if (!parsed) return;
    setParsed({
      ...parsed,
      rows: parsed.rows.filter((_, i) => i !== idx),
    });
  };

  const doImport = async () => {
    if (!parsed || parsed.rows.length === 0) {
      toast.error("Keine Spieler zum Importieren.");
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post("/players/bulk", {
        team_id: teamId,
        players: parsed.rows,
      });
      toast.success(`${data.count} Spieler importiert`);
      onImported?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
    }
  };

  const example = "name,position,jersey_number\nMax Mustermann,Sechser,8\nLena Schmidt,Torwart,1";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="csv-import-modal"
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#111214] border border-white/10 rounded-md p-6 sm:p-8"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-2">
              / CSV-IMPORT
            </div>
            <h2 className="font-heading font-black text-2xl leading-tight">
              Spieler in einem Rutsch anlegen
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white p-1"
            aria-label="Schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!parsed && (
          <>
            <div className="mb-4 p-4 border border-white/10 rounded-md bg-white/5">
              <div className="font-display text-[10px] tracking-widest text-[#CCFF00] mb-2">
                CSV-FORMAT
              </div>
              <div className="text-xs text-white/70 mb-2">
                Spalten: <b>name</b> (Pflicht), <b>position</b> (optional), <b>jersey_number</b> (optional).
                Trennzeichen: <code>,</code> <code>;</code> oder Tab. Header-Zeile ist optional.
              </div>
              <pre className="text-xs bg-black/40 border border-white/10 rounded p-3 text-white/80 overflow-x-auto">
{example}
              </pre>
            </div>

            <label
              className="block cursor-pointer border-2 border-dashed border-white/15 hover:border-[#CCFF00]/40 rounded-md p-6 text-center transition-colors mb-4"
              data-testid="csv-upload-drop"
            >
              <Upload className="w-6 h-6 text-[#CCFF00] mx-auto mb-2" />
              <div className="font-heading font-bold">CSV-Datei auswählen</div>
              <div className="text-xs text-white/50 mt-1">oder unten einfügen</div>
              <input
                type="file"
                accept=".csv,text/csv,text/plain"
                onChange={handleFile}
                className="hidden"
                data-testid="csv-file-input"
              />
            </label>

            <div>
              <label className="font-display text-xs tracking-widest text-white/60 block mb-2">
                CSV EINFÜGEN
              </label>
              <textarea
                data-testid="csv-textarea"
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                rows={7}
                placeholder={example}
                className="w-full bg-[#0a0a0b] border border-white/10 rounded-md px-3 py-3 text-white font-mono text-xs focus:border-[#CCFF00] outline-none resize-none"
              />
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={onClose} className="btn-ghost flex-1 justify-center">
                Abbrechen
              </button>
              <button
                data-testid="csv-parse-btn"
                onClick={handleParse}
                disabled={!csvText.trim()}
                className="btn-primary flex-1 justify-center disabled:opacity-40"
              >
                <FileText className="w-4 h-4" />
                Vorschau
              </button>
            </div>
          </>
        )}

        {parsed && (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading font-bold">
                Vorschau · {parsed.rows.length} Spieler
              </div>
              <button
                data-testid="csv-back-btn"
                onClick={() => setParsed(null)}
                className="text-xs text-white/50 hover:text-[#CCFF00]"
              >
                ← Bearbeiten
              </button>
            </div>

            {parsed.errors.length > 0 && (
              <div className="mb-3 p-3 border border-red-500/40 bg-red-500/5 rounded-md flex gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-red-300">
                  {parsed.errors.join(" · ")}
                </div>
              </div>
            )}

            {parsed.rows.length === 0 ? (
              <div className="p-6 text-center text-white/50 border border-white/10 rounded-md">
                Keine gültigen Zeilen gefunden.
              </div>
            ) : (
              <div className="border border-white/10 rounded-md overflow-hidden max-h-[40vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#111214]">
                    <tr className="border-b border-white/10 text-left font-display text-[10px] tracking-widest text-white/40">
                      <th className="p-2 pl-3">#</th>
                      <th className="p-2">Name</th>
                      <th className="p-2">Position</th>
                      <th className="p-2 text-right pr-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.map((r, i) => (
                      <tr
                        key={i}
                        data-testid={`csv-preview-row-${i}`}
                        className="border-b border-white/5 last:border-0"
                      >
                        <td className="p-2 pl-3 font-display text-white/50 text-xs">
                          {r.jersey_number ? `#${r.jersey_number}` : "–"}
                        </td>
                        <td className="p-2 font-heading font-bold">{r.name}</td>
                        <td className="p-2 text-white/70">{r.position || "–"}</td>
                        <td className="p-2 text-right pr-3">
                          <button
                            onClick={() => removeRow(i)}
                            data-testid={`csv-preview-remove-${i}`}
                            className="text-white/30 hover:text-red-400 p-1"
                            title="Entfernen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button onClick={onClose} className="btn-ghost flex-1 justify-center">
                Abbrechen
              </button>
              <button
                data-testid="csv-import-submit"
                onClick={doImport}
                disabled={parsed.rows.length === 0 || importing}
                className="btn-primary flex-1 justify-center disabled:opacity-40"
              >
                <Upload className="w-4 h-4" />
                {importing ? "Importiere …" : `${parsed.rows.length} importieren`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
